import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Capability } from './capabilities.constants';
import { PermissionsService } from './permissions.service';

/**
 * Build a route guard that allows activation when the current user has
 * `cap` (or wildcard / super-admin). Use it in route definitions:
 *
 *     { path: 'rbac',
 *       canActivate: [requireCapability(CAPS.RBAC.MANAGE)],
 *       loadChildren: () => import('./...').then(m => m.Module) }
 *
 * Failure behavior:
 *   - The guard navigates to `/app/404` when the user is authenticated but
 *     missing the cap. We do NOT log them out — that's `authGuard`'s job.
 */
export function requireCapability(cap: Capability): CanActivateFn {
  return () => {
    const perms = inject(PermissionsService);
    const router = inject(Router);
    if (perms.hasCap(cap)) {
      return true;
    }
    router.navigate(['/app/404']);
    return false;
  };
}

/**
 * Variant: allow if ANY of the listed caps are granted.
 */
export function requireAnyCapability(caps: Capability[]): CanActivateFn {
  return () => {
    const perms = inject(PermissionsService);
    const router = inject(Router);
    if (perms.hasAnyCap(caps)) {
      return true;
    }
    router.navigate(['/app/404']);
    return false;
  };
}
